import {
  getDevice,
  getDeviceState,
  getDeviceStates,
  getGroupedDevs,
  getDeviceConfig,
  getDeviceConfigs,
  getDeviceSources,
  getSYDeviceSetting,
  getSdmproSetting,
  updateDevice,
  updateDeviceNameAndRegister,
  updateDeviceWarningRule,
  updateDeviceMark,
  updateDeviceSmoothConfig,
  updateDeviceBranch,
  updateDevicePosition,
  updateDeviceBadNodes,
  updateSdmproMode,
  mountDevice,
  resetDeviceDeformation,
  setDeviceJitters,
  deleteDevice,
  deleteDeviceSource,
} from '@/api';
import { ADM, SDM, SDMX, SDMPRO, HDA2, HDA3 } from '@/utils/constants';

const localState = {
  devices: [],
  deviceTypes: [], // 项目已挂载设备类型
  deviceSources: [], // ADM设备源
  deviceConfigs: [], // ADM设备校准文件配置

  /**
   * 设备基本状态列表 SimpleDeviceState[]
   */
  deviceStates: [],

  /**
   * 设备状态详情缓存 { [id: number]: DeviceState }
   * 查看详情先从缓存中查找，未击中则调API
   * TODO socketIO监听有状态更新则同步状态
   */
  deviceStatesCache: {},

  /**
   * 项目首页示意图选中设备
   */
  homePageSelectedDeviceId: '',

  /**
   * 项目设备列表更新时间，用于被监听，触发设备列表更新
   */
  devicesListUpdatedAt: new Date(),

  // 所有的设备中和设备中的测点
  allDevice: [],
};

const getters = {
  getDeviceById: (state) => {
    return (id) => {
      return state.devices.find(device => device.id === id);
    };
  },
  getDeviceStateById: (state) => {
    return (id) => {
      return state.deviceStates.find(d => d.id === id);
    };
  },
  getDevicesByIds: (state) => {
    return (ids) => {
      return state.devices.filter(d => ids.includes(d.id));
    };
  },
  ADMDevices: state => state.devices.filter(d => d.type === ADM),
  SDMDevices: state => state.devices.filter(d => d.type === SDM), // 单点位移计
  SDMXDevices: state => state.devices.filter(d => d.type === SDMX), // 振动计
  SDMPRODevices: state => state.devices.filter(d => d.type === SDMPRO), // 振动计
  Hda2Devices: state => state.devices.filter(d => d.type === HDA2), // 渗压计
  Hda3Devices: state => state.devices.filter(d => d.type === HDA3), // 水位计
  deviceTypes: state => state.deviceTypes,
};

const actions = {
  /**
   * 获取项目下所有设备基本状态
   */
  async initDeviceStates({ commit, rootState }) {
    const projectId = rootState.project.currentProject.id;
    if (!projectId) return;
    const res = await getDeviceStates({ projectId });
    const { deviceStates } = res.data;
    if (
      (deviceStates
      && deviceStates.length > 0
      && rootState.device.deviceStates
      && rootState.device.deviceStates.length > 0
      && deviceStates[0].projectId !== rootState.device.deviceStates[0].projectId
      )
      || rootState.device.deviceStates.length === 0
    ) {
      commit('setDeviceStates', { deviceStates });
    }
    // 切换项目清空设备列表
    commit('setDevices', { devices: [] });
  },

  /**
   * 获取单个设备详细状态
   */
  async findOrGetDeviceState({ state, commit }, { deviceId }) {
    const target = state.deviceStatesCache[deviceId];
    if (target) return target;

    const res = await getDeviceState({ deviceId });
    const { code, msg, deviceState } = res.data;
    if (code !== 0) {
      throw new Error(msg);
    }
    commit('updateDeviceStatesCache', { deviceState });
    return deviceState;
  },

  /**
   * 通过设备号 deviceName 从设备列表中找到设备实例，找不到则调用接口获取
   */
  async findOrGetDevice({ state }, { deviceName }) {
    if (!deviceName) return null;
    let device = state.devices.find(item => item.name === deviceName);
    if (device) return device;
    const deviceState = state.deviceStates.find(item => item.name === deviceName);
    if (!deviceState) throw new Error(`当前项目下找不到设备 ${deviceName}`);
    const res = await getDevice({ id: deviceState.id });
    device = res.data.device;
    return device;
  },

  async initDeviceSource({ commit }) {
    const res = await getDeviceSources();
    const { deviceSources } = res.data;
    commit('setDeviceSources', { deviceSources });
  },
  async initDeviceConfig({ commit }) {
    const res = await getDeviceConfigs();
    const { deviceConfigs } = res.data;
    commit('setDeviceConfigs', { deviceConfigs });
  },
  async deleteDeviceSource({ state }, { deviceSourceId }) {
    const res = await deleteDeviceSource({ id: deviceSourceId });
    if (res.data.code !== 0) throw new Error(res.data.msg);
    const index = state.deviceSources.findIndex(s => s.id === deviceSourceId);
    state.deviceSources.splice(index, 1);
  },
  // 挂载设备后需要更新源设备状态, 并更新该项目下的设备信息
  async mountDevice({ state, commit, rootState, dispatch }, { options }) {
    const res = await mountDevice(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);

    const deviceSource = state.deviceSources.find(d => d.name === options.name);
    if (deviceSource) { // 渗压计没有源设备
      deviceSource.mounted = true;
    }

    const projectIdOption = {
      projectId: rootState.project.currentProject.id,
    };
    commit('updateDevicesList');
    await dispatch('menu/initMenus', projectIdOption, { root: true });
  },
  async unmountDevice({ state, commit, rootState, dispatch }, { device }) {
    const { id, DeviceSourceId } = device;
    const res = await deleteDevice({ id });
    if (res.data.code !== 0) throw new Error(res.data.msg);

    const deviceSource = state.deviceSources.find(d => d.id === DeviceSourceId);
    if (deviceSource) {
      deviceSource.mounted = false;
    }

    const projectIdOption = {
      projectId: rootState.project.currentProject.id,
    };
    commit('updateDevicesList');
    await dispatch('menu/initMenus', projectIdOption, { root: true });
  },

  async fetchGroupedDevices({ commit },
    { projectId, type, branchId, departmentId }) {
    const options = { id: projectId, type, branchId, departmentId };
    const res = await getGroupedDevs(options);
    const { code, msg, devices } = res.data;
    if (code !== 0) throw new Error(msg);
    commit('setDevices', { devices }); // append导致数据重复
    commit('setAllDevice', { branchId, projectId, departmentId, type, children: devices });
    return devices;
  },
  async updateDevice({ commit }, options) {
    const res = await updateDevice(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDevice', { device: res.data.device });
    commit('updateDevicesList');
    return res.data;
  },

  // 测点设备替换（更新设备号及网络识别码）
  async replaceDevice({ commit }, options) {
    const res = await updateDeviceNameAndRegister(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDevice', { device: res.data.device });
    commit('updateDevicesList');
  },
  async updateDeviceWarningRule(_context, options) {
    const res = await updateDeviceWarningRule(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
  },
  async updateDeviceSmoothConfig({ commit }, options) {
    const res = await updateDeviceSmoothConfig(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDevice', { device: res.data.device });
    return res.data;
  },
  async updateDeviceMark({ commit }, options) {
    const res = await updateDeviceMark(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDeviceMark', { device: res.data.device });
  },
  async getDeviceConfig(_context, { deviceName }) {
    const res = await getDeviceConfig({ deviceName });
    return res.data.deviceConfig;
  },
  async updateDeviceBranch({ commit }, { deviceId, branchId, departmentId }) {
    const res = await updateDeviceBranch({ deviceId, branchId, departmentId });
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDeviceBranch', { device: res.data.device });
  },
  async getSYDeviceSetting(_context, { deviceId }) {
    const res = await getSYDeviceSetting({ deviceId });
    return res.data.syDeviceSetting;
  },
  async getSdmproSetting(_context, { deviceId }) {
    const res = await getSdmproSetting({ deviceId });
    return res.data.sdmproSetting;
  },
  async setDeviceJitters(_context, { deviceId, startJitterAt, endJitterAt, jitters }) {
    const res = await setDeviceJitters({ deviceId, startJitterAt, endJitterAt, jitters });
    if (res.data.code !== 0) throw new Error(res.data.msg);
  },
  async resetDeformation(_context, { deviceId }) {
    const res = await resetDeviceDeformation({ deviceId });
    if (res.data.code !== 0) throw new Error(res.data.msg);
  },
  async updateDevicePosition({ commit }, { deviceId, lng, lat }) {
    const res = await updateDevicePosition({ deviceId, lng, lat });
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDevice', { device: res.data.device });
  },
  async updateSdmproMode(_context, options) {
    const res = await updateSdmproMode(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
  },
  async submitBadNodes({ commit }, options) {
    const res = await updateDeviceBadNodes(options);
    if (res.data.code !== 0) throw new Error(res.data.msg);
    commit('updateDevice', { device: res.data.device });
  },
};

const mutations = {
  setDevices(state, { devices }) {
    state.devices = devices;
  },
  setAllDevice(state, data) {
    if (
      !state.allDevice.find(item => item.BranchId === data.branchId && item.type === data.type)
    ) {
      state.allDevice = [...state.allDevice, ...[data]];
    }
  },
  appendDevices(state, { devices }) {
    state.devices = [...state.devices, ...devices];
  },
  setDeviceTypes(state, { deviceTypes }) {
    state.deviceTypes = deviceTypes;
  },
  setDeviceSources(state, { deviceSources }) {
    state.deviceSources = deviceSources;
  },
  setDeviceStates(state, { deviceStates }) {
    state.deviceStates = deviceStates;
  },
  setDeviceConfigs(state, { deviceConfigs }) {
    state.deviceConfigs = deviceConfigs;
  },
  updateDevice(state, { device }) {
    const target = state.devices.find(d => d.id === device.id);
    if (target) Object.assign(target, device);
  },
  // 更新设备采集状态
  updateDeviceState(state, { deviceState }) {
    const target = state.deviceStates.find(dstate => dstate.deviceId === deviceState.deviceId);
    if (target) Object.assign(target, deviceState);
  },
  // 更新设备在项目首页标记点坐标
  updateDeviceMark(state, { device }) {
    const target = state.devices.find(d => d.id === device.id);
    const { markTop, markLeft } = device;
    if (target) Object.assign(target, { markTop, markLeft });
  },
  // 更新设备分组
  updateDeviceBranch(state, { device }) {
    const target = state.devices.find(d => d.id === device.id);
    const { BranchId, DepartmentId } = device;
    if (target) Object.assign(target, { BranchId, DepartmentId });
  },
  // 项目首页示意图选中的设备
  homePageDeviceSelect(state, { deviceId }) {
    state.homePageSelectedDeviceId = deviceId;
  },

  updateDeviceStatesCache(state, { deviceState }) {
    state.deviceStatesCache[deviceState.deviceId] = deviceState;
  },

  updateDevicesList(state) {
    state.devicesListUpdatedAt = new Date();
  },
};

export default {
  getters,
  actions,
  mutations,
  state: localState,
  namespaced: true,
};

